import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

import {
  Database,
  Settings,
  Play,
  Cloud,
  RefreshCw,
  CheckCircle,
  Sliders,
  Rocket,
  ClipboardCheck,
  ArrowRightLeft,
  Tag,
  LogOut,
  User,
  Truck,
} from "lucide-react";
import { Button } from "./components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { SearchableSelect } from "./components/ui/searchable-select";
import { Badge } from "./components/ui/badge";
import AdvancedResultsDisplay from "./components/AdvancedResultsDisplay";
import AzureDeviceManager from "./components/AzureDeviceManager";
import OnboardingManager from "./components/OnboardingManager";
import OnboardingStatus from "./components/OnboardingStatus";
import DeviceGroups from "./components/DeviceGroups";
import MigrationPanel from "./components/MigrationPanel";
import ReleaseVersionsManager from "./components/ReleaseVersionsManager";
import CustomConfigManager from "./components/CustomConfigManager";
import VinDecoderCard from "./components/VinDecoderCard";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL || "";
axios.defaults.withCredentials = true;

function AppContent() {
  const { user, logout, isAdmin, isOperations, isViewer, canEdit } = useAuth();
  const [analyzers, setAnalyzers] = useState([]);
  const [selectedAnalyzers, setSelectedAnalyzers] = useState(["general"]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [maxPages, setMaxPages] = useState(50);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Cached messages state
  const [cachedMessages, setCachedMessages] = useState(null);
  const [cachedDateRange, setCachedDateRange] = useState(null);

  // Analyze tab fleet + device picker state
  const [analyzeGroups, setAnalyzeGroups] = useState([]);
  const [analyzeSelectedGroup, setAnalyzeSelectedGroup] = useState("");
  const [analyzeGroupDevices, setAnalyzeGroupDevices] = useState([]);
  const [analyzeSelectedDevices, setAnalyzeSelectedDevices] = useState([]);

  // The count comes along because picking a fleet to analyze is mostly a question of
  // which one has the devices in it.
  const analyzeFleetOptions = useMemo(
    () => analyzeGroups.map((g) => ({
      value: g.name,
      label: `${g.name} (${g.device_count ?? 0})`,
    })),
    [analyzeGroups]
  );

  // Azure Management state (persisted across tab changes)
  const [azureSearchSerial, setAzureSearchSerial] = useState("");
  const [azureSearchResult, setAzureSearchResult] = useState(null);
  const [azureBatchResults, setAzureBatchResults] = useState(null);

  // Onboarding state (persisted across tab changes)
  const [onboardingState, setOnboardingState] = useState({
    // Remote Wakeup
    deviceId: "",
    deviceStatus: null,
    error: null,
    success: null,
    // MDVR Config
    configDeviceId: "",
    configError: null,
    configSuccess: null,
    configResult: null,
    useBatchMode: false,
    selectedBatchDevices: [],
    batchResults: null,
    currentConfig: { MDVR: {} },
    // Config Binding
    bindingDeviceId: "",
    bindingError: null,
    bindingSuccess: null,
    bindingResult: null,
    bindingConfig: {},
    useBindingBatchMode: false,
    selectedBindingDevices: [],
    bindingBatchResults: null,
    bindingResultExpanded: true,
    bindingBatchResultsExpanded: true,
  });

  // Onboarding Status state (persisted across tab changes)
  const [onboardingStatusState, setOnboardingStatusState] = useState({
    results: [],
    loading: false,
    error: null,
    filters: {
      serialNumber: "",
      azureStatus: "all",
      completionStatus: "all",
      busActive: "all",
      busSpeed: "all",
    },
    sortConfig: { key: null, direction: "asc" },
    showFilters: false,
    selectedGroup: "all-groups",
    availableGroups: [],
    visibleColumns: {
      status: true,
      serialNumber: true,
      azureStatus: true,
      deviceType: true,
      upgradeType: true,
      upgradeStatus: true,
      configStatus: true,
      ltaVersion: true,
      imageVersion: true,
      baseImage: true,
      lastUpdate: true,
      imei: true,
      iccid: true,
      vin: true,
      busActive: true,
      busSpeed: true,
      canSignals: true,
    },
  });

  // Migration Panel state (persisted across tab changes)
  const [migrationPanelState, setMigrationPanelState] = useState({
    selectedGroups: [],
    selectedRelease: null,
    autoRefresh: true,
    lastRefresh: null,
    statusGroupFilter: "all",
    taskResults: [],
  });

  // Tab state
  const [activeMainTab, setActiveMainTab] = useState(
    isViewer() ? "onboarding" : "devices",
  );
  const [activeOnboardingTab, setActiveOnboardingTab] = useState(
    isViewer() ? "status" : "migration"
  );

  // Profile menu state
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  useEffect(() => {
    loadAnalyzers();
    setDefaultDates();
  }, []);

  // Load fleet list for Analyze tab
  useEffect(() => {
    axios
      .get("/api/device-groups")
      .then((r) => setAnalyzeGroups(r.data.groups || []))
      .catch(() => {});
  }, []);

  // Load devices when fleet selection changes
  useEffect(() => {
    if (!analyzeSelectedGroup) {
      setAnalyzeGroupDevices([]);
      return;
    }
    axios
      .get(`/api/device-groups/${encodeURIComponent(analyzeSelectedGroup)}`)
      .then((r) => setAnalyzeGroupDevices(r.data.devices || []))
      .catch(() => setAnalyzeGroupDevices([]));
  }, [analyzeSelectedGroup]);

  const setDefaultDates = () => {
    const today = new Date();
    // Format for datetime-local input: YYYY-MM-DDTHH:MM
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    setStartDate(`${year}-${month}-${day}T00:00`);
    setEndDate(`${year}-${month}-${day}T23:59`);
  };

  // Convert datetime-local format to API format
  const formatDateForApi = (dateStr) => {
    if (!dateStr) return "";
    // datetime-local gives us YYYY-MM-DDTHH:MM, we need YYYY-MM-DDTHH:MM:SS.000
    return `${dateStr}:00.000`;
  };

  const loadAnalyzers = async () => {
    try {
      const response = await axios.get("/api/analyzers");
      setAnalyzers(response.data.analyzers);
    } catch (err) {
      console.error("Error loading analyzers:", err);
    }
  };

  const handleNavigateToFleetStatus = (groupName) => {
    if (!groupName) return;
    setActiveMainTab("onboarding");
    setActiveOnboardingTab("status");
    setOnboardingStatusState((prev) => ({
      ...prev,
      selectedGroup: groupName,
      results: [],
      error: null,
    }));
  };

  const handleAnalyzerSelect = async (analyzerId) => {
    setSelectedAnalyzers([analyzerId]);

    // Auto-run analysis if cache is valid
    if (
      cachedMessages &&
      cachedDateRange &&
      analyzeSelectedDevices.length > 0
    ) {
      // Check if selected devices are in cache
      const allDevicesInCache = analyzeSelectedDevices.every(
        (d) => cachedMessages[d] !== undefined,
      );
      if (allDevicesInCache) {
        setLoading(true);
        setError(null);

        try {
          const filteredMessages = {};
          analyzeSelectedDevices.forEach((device) => {
            if (cachedMessages[device]) {
              filteredMessages[device] = cachedMessages[device];
            }
          });

          const response = await axios.post("/api/analyze-cached", {
            device_messages: filteredMessages,
            start_date: cachedDateRange.start,
            end_date: cachedDateRange.end,
            analyzers: [analyzerId],
          });

          setResults(response.data);
        } catch (err) {
          console.error("Error analyzing:", err);
          setError(err.response?.data?.error || "Analysis failed");
        } finally {
          setLoading(false);
        }
      }
    }
  };

  // Check if cache is valid for current selection
  const isCacheValid = () => {
    if (!cachedMessages || !cachedDateRange) return false;
    // Compare formatted dates (API format) since cachedDateRange stores API format
    const formattedStart = formatDateForApi(startDate);
    const formattedEnd = formatDateForApi(endDate);
    if (
      cachedDateRange.start !== formattedStart ||
      cachedDateRange.end !== formattedEnd
    )
      return false;
    // Check if all selected devices are in cache
    return analyzeSelectedDevices.every((d) => cachedMessages[d] !== undefined);
  };

  // Fetch data from cloud and cache it
  const handleFetchData = async () => {
    if (analyzeSelectedDevices.length === 0) {
      setError("Please select at least one device");
      return;
    }

    setFetchingData(true);
    setError(null);

    try {
      const response = await axios.post("/api/fetch-all-messages", {
        devices: analyzeSelectedDevices,
        start_date: formatDateForApi(startDate),
        end_date: formatDateForApi(endDate),
        max_pages: maxPages,
      });

      setCachedMessages(response.data.device_messages);
      setCachedDateRange({
        start: response.data.start_date,
        end: response.data.end_date,
      });
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.response?.data?.error || "Failed to fetch data");
    } finally {
      setFetchingData(false);
    }
  };

  // Run analysis using cached data
  const handleAnalyze = async () => {
    if (analyzeSelectedDevices.length === 0) {
      setError("Please select at least one device");
      return;
    }

    // If no cache or cache is invalid, fetch first then analyze
    if (!isCacheValid()) {
      setFetchingData(true);
      setError(null);

      try {
        const fetchResponse = await axios.post("/api/fetch-all-messages", {
          devices: analyzeSelectedDevices,
          start_date: formatDateForApi(startDate),
          end_date: formatDateForApi(endDate),
          max_pages: maxPages,
        });

        setCachedMessages(fetchResponse.data.device_messages);
        setCachedDateRange({
          start: fetchResponse.data.start_date,
          end: fetchResponse.data.end_date,
        });

        // Now run analysis with fetched data
        setFetchingData(false);
        setLoading(true);

        const analyzeResponse = await axios.post("/api/analyze-cached", {
          device_messages: fetchResponse.data.device_messages,
          start_date: fetchResponse.data.start_date,
          end_date: fetchResponse.data.end_date,
          analyzers: selectedAnalyzers,
        });

        setResults(analyzeResponse.data);
      } catch (err) {
        console.error("Error:", err);
        setError(err.response?.data?.error || "Analysis failed");
      } finally {
        setFetchingData(false);
        setLoading(false);
      }
      return;
    }

    // Use cached data
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const filteredMessages = {};
      analyzeSelectedDevices.forEach((device) => {
        if (cachedMessages[device]) {
          filteredMessages[device] = cachedMessages[device];
        }
      });

      const response = await axios.post("/api/analyze-cached", {
        device_messages: filteredMessages,
        start_date: cachedDateRange.start,
        end_date: cachedDateRange.end,
        analyzers: selectedAnalyzers,
      });

      setResults(response.data);
    } catch (err) {
      console.error("Error analyzing:", err);
      setError(err.response?.data?.error || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  // Clear cache
  const handleClearCache = () => {
    setCachedMessages(null);
    setCachedDateRange(null);
    setResults(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Database className="w-10 h-10 text-blue-600" />
              <div>
                <h1 className="text-4xl font-bold text-gray-900">
                  Migration Engine
                </h1>
                <p className="text-gray-600">
                  Manage fleet migrations and IoT telemetry insights
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <div className="font-medium text-sm text-gray-900">
                    {user?.name}
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-xs ${
                      isAdmin()
                        ? "bg-red-50 text-red-700 border-red-300"
                        : isOperations()
                          ? "bg-blue-50 text-blue-700 border-blue-300"
                          : "bg-gray-50 text-gray-700 border-gray-300"
                    }`}
                  >
                    {isAdmin()
                      ? "Admin"
                      : isOperations()
                        ? "Operations"
                        : "Viewer"}
                  </Badge>
                </div>
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex items-center gap-1 h-10 w-10 p-0 justify-center"
                  >
                    <User className="w-5 h-5" />
                  </Button>

                  {showProfileMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-50">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            logout();
                          }}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                        >
                          <LogOut className="w-4 h-4" />
                          Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Tabs
          value={activeMainTab}
          onValueChange={setActiveMainTab}
          className="space-y-4"
        >
          <TabsList
            className={`grid w-full ${isViewer() ? "grid-cols-1" : "grid-cols-3"} lg:w-[600px]`}
          >
            {!isViewer() && (
              <TabsTrigger value="devices" className="flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Manage Fleets
              </TabsTrigger>
            )}
            <TabsTrigger value="onboarding" className="flex items-center gap-2">
              <Rocket className="w-4 h-4" />
              Onboarding
            </TabsTrigger>
            {isAdmin() && (
              <TabsTrigger value="advanced" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Advanced Configs
              </TabsTrigger>
            )}
          </TabsList>

          {/* Manage Fleets Tab */}
          <TabsContent value="devices" className="space-y-4">
            <DeviceGroups onNavigateToFleet={handleNavigateToFleetStatus} />
          </TabsContent>

          {/* Onboarding Tab - Contains sub-tabs */}
          <TabsContent value="onboarding" className="space-y-4">
            <Tabs
              value={activeOnboardingTab}
              onValueChange={setActiveOnboardingTab}
              className="space-y-4"
            >
              <TabsList className={`grid w-full ${isViewer() ? "grid-cols-1 lg:w-[210px]" : "grid-cols-2 lg:w-[420px]"}`}>
                {!isViewer() && (
                  <TabsTrigger
                    value="migration"
                    className="flex items-center gap-2"
                  >
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-slate-300 text-xs font-semibold">
                      1
                    </span>
                    <ArrowRightLeft className="w-4 h-4" />
                    Migration
                  </TabsTrigger>
                )}
                <TabsTrigger value="status" className="flex items-center gap-2">
                  {!isViewer() && (
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-slate-300 text-xs font-semibold">
                      2
                    </span>
                  )}
                  <ClipboardCheck className="w-4 h-4" />
                  Onboarding Status
                </TabsTrigger>
              </TabsList>

              {/* Migration Sub-Tab */}
              {!isViewer() && (
                <TabsContent value="migration" className="space-y-4">
                  <MigrationPanel
                    state={migrationPanelState}
                    setState={setMigrationPanelState}
                  />
                </TabsContent>
              )}

              {/* Onboarding Status Sub-Tab */}
              <TabsContent value="status" className="space-y-4">
                <OnboardingStatus
                  state={onboardingStatusState}
                  setState={setOnboardingStatusState}
                />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Advanced Configs Tab - Contains sub-tabs */}
          <TabsContent value="advanced" className="space-y-4">
            <Tabs defaultValue="azure" className="space-y-4">
              <TabsList className="grid w-full grid-cols-5 lg:w-[1000px]">
                <TabsTrigger value="azure" className="flex items-center gap-2">
                  <Cloud className="w-4 h-4" />
                  IoT Management
                </TabsTrigger>
                <TabsTrigger
                  value="analyze"
                  className="flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  Analyze
                </TabsTrigger>
                <TabsTrigger
                  value="configuration"
                  className="flex items-center gap-2"
                >
                  <Sliders className="w-4 h-4" />
                  Configuration
                </TabsTrigger>
                <TabsTrigger
                  value="releases"
                  className="flex items-center gap-2"
                >
                  <Tag className="w-4 h-4" />
                  Release Versions
                </TabsTrigger>
              </TabsList>

              {/* Azure Management Sub-Tab */}
              <TabsContent value="azure" className="space-y-4">
                <AzureDeviceManager
                  searchSerial={azureSearchSerial}
                  setSearchSerial={setAzureSearchSerial}
                  searchResult={azureSearchResult}
                  setSearchResult={setAzureSearchResult}
                  batchResults={azureBatchResults}
                  setBatchResults={setAzureBatchResults}
                />
              </TabsContent>

              {/* Analyze Sub-Tab */}
              <TabsContent value="analyze" className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Fleet + Device Selection */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Database className="w-5 h-5" />
                        Select Devices
                      </CardTitle>
                      <CardDescription>
                        Pick a fleet, then choose devices to analyze
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-1">
                        <label
                          className="text-sm font-medium"
                          htmlFor="analyzeFleet"
                        >
                          Fleet
                        </label>
                        <SearchableSelect
                          id="analyzeFleet"
                          options={analyzeFleetOptions}
                          value={analyzeSelectedGroup}
                          onChange={(name) => {
                            setAnalyzeSelectedGroup(name);
                            setAnalyzeSelectedDevices([]);
                          }}
                          placeholder="Search or select a fleet"
                          emptyMessage="No fleets found"
                        />
                      </div>
                      {analyzeSelectedGroup && (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium">
                              Devices
                            </label>
                            {analyzeGroupDevices.length > 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setAnalyzeSelectedDevices(
                                    analyzeSelectedDevices.length ===
                                      analyzeGroupDevices.length
                                      ? []
                                      : analyzeGroupDevices.map(
                                          (d) => d.serial,
                                        ),
                                  )
                                }
                              >
                                {analyzeSelectedDevices.length ===
                                analyzeGroupDevices.length
                                  ? "Deselect All"
                                  : "Select All"}
                              </Button>
                            )}
                          </div>
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {analyzeGroupDevices.length === 0 ? (
                              <p className="text-sm text-muted-foreground">
                                No devices in this fleet.
                              </p>
                            ) : (
                              analyzeGroupDevices.map((device) => (
                                <label
                                  key={device.serial}
                                  className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-accent transition-colors"
                                >
                                  <input
                                    type="checkbox"
                                    checked={analyzeSelectedDevices.includes(
                                      device.serial,
                                    )}
                                    onChange={() =>
                                      setAnalyzeSelectedDevices((prev) =>
                                        prev.includes(device.serial)
                                          ? prev.filter(
                                              (s) => s !== device.serial,
                                            )
                                          : [...prev, device.serial],
                                      )
                                    }
                                    className="w-4 h-4"
                                  />
                                  <span className="font-medium text-sm">
                                    {device.serial}
                                  </span>
                                </label>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Analyzer Selection */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Select Analyzer</CardTitle>
                      <CardDescription>
                        Choose an analysis type to run
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {analyzers.map((analyzer) => (
                          <div
                            key={analyzer.id}
                            onClick={() => handleAnalyzerSelect(analyzer.id)}
                            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              selectedAnalyzers.includes(analyzer.id)
                                ? "border-blue-500 bg-blue-50"
                                : "border-border hover:border-blue-300 hover:bg-accent"
                            }`}
                          >
                            <div className="font-medium">{analyzer.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {analyzer.description}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Date Range Configuration */}
                <Card>
                  <CardHeader>
                    <CardTitle>Date Range</CardTitle>
                    <CardDescription>
                      Configure the time period for analysis
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="startDate">Start Date & Time</Label>
                        <Input
                          id="startDate"
                          type="datetime-local"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="cursor-pointer"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="endDate">End Date & Time</Label>
                        <Input
                          id="endDate"
                          type="datetime-local"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="cursor-pointer"
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="maxPages" className="whitespace-nowrap">
                          Max Pages:
                        </Label>
                        <Input
                          id="maxPages"
                          type="number"
                          min="1"
                          max="50"
                          value={maxPages}
                          onChange={(e) => {
                            const next = parseInt(e.target.value) || 1;
                            setMaxPages(Math.max(1, Math.min(50, next)));
                          }}
                          className="w-20"
                        />
                        <span className="text-sm text-gray-500">
                          ({maxPages * 100} msgs/device)
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Cache Status */}
                {cachedMessages && (
                  <Card className="border-green-500 bg-green-50">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="w-5 h-5" />
                          <div>
                            <p className="font-medium">Data Cached</p>
                            <p className="text-sm text-green-600">
                              {Object.keys(cachedMessages).length} device(s) ·{" "}
                              {Object.values(cachedMessages).reduce(
                                (sum, msgs) => sum + msgs.length,
                                0,
                              )}{" "}
                              messages · {cachedDateRange?.start?.split("T")[0]}{" "}
                              to {cachedDateRange?.end?.split("T")[0]}
                            </p>
                          </div>
                        </div>
                        <Button
                          onClick={handleClearCache}
                          variant="outline"
                          size="sm"
                          className="text-green-700 border-green-500 hover:bg-green-100"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Clear Cache
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Action Buttons */}
                <div className="flex gap-4 flex-wrap">
                  <Button
                    onClick={handleFetchData}
                    disabled={
                      fetchingData || analyzeSelectedDevices.length === 0
                    }
                    variant="outline"
                    className="flex items-center gap-2"
                    size="lg"
                  >
                    <Database className="w-4 h-4" />
                    {fetchingData ? "Fetching..." : "Fetch Data"}
                  </Button>
                  <Button
                    onClick={handleAnalyze}
                    disabled={
                      loading ||
                      fetchingData ||
                      analyzeSelectedDevices.length === 0
                    }
                    className="flex items-center gap-2"
                    size="lg"
                  >
                    <Play className="w-4 h-4" />
                    {loading
                      ? "Analyzing..."
                      : fetchingData
                        ? "Fetching..."
                        : isCacheValid()
                          ? "Run Analysis (Cached)"
                          : "Run Analysis"}
                  </Button>
                </div>

                {/* Error Display */}
                {error && (
                  <Card className="border-destructive">
                    <CardContent className="pt-6">
                      <p className="text-destructive font-medium">{error}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Results Display */}
                {results && <AdvancedResultsDisplay results={results} />}
              </TabsContent>

              {/* Configuration Sub-Tab */}
              <TabsContent value="configuration" className="space-y-4">
                <OnboardingManager
                  state={onboardingState}
                  setState={setOnboardingState}
                />
                <VinDecoderCard />
                <CustomConfigManager />
              </TabsContent>

              {/* Release Versions Sub-Tab */}
              <TabsContent value="releases" className="space-y-4">
                <ReleaseVersionsManager />
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function App() {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      const base = process.env.REACT_APP_API_BASE_URL || "";
      window.location.href = base + "/api/auth/login";
    }
  }, [loading, user]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return <AppContent />;
}

export default function AppWithAuth() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}
